import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {EMPTY, Subject} from 'rxjs';
import {catchError, debounceTime, takeUntil} from 'rxjs/operators';
import {PlantStatisticalProfile, PodStatisticalProfile, UserType} from 'smag-commons/cfg/hierarchy';
import {PodStatisticalProfileTemplate, RateMode, TimeSlot, WindPlantStatisticalProfileTemplate} from 'smag-commons/cfg';
import {PodStatisticalProfileTemplateType, PvPlantStatisticalProfileTemplate} from 'smag-commons/cfg/templates';
import {MatSelectChange} from '@angular/material/select';
import {SmagApiService} from '../../../api/smag-api.service';
import {errorCode, isDefined, round} from '../../../commons/commons';
import {ErrorCode} from '../../../api/errors';
import {I18NService} from '../../../i18n/i18n.service';
import {
    StatisticalProfilePlantResource,
    StatisticalProfilePodResource,
    StatisticalProfileResource,
    StatisticalProfileResourceType
} from '../statistical-profile.component';
import {limitDecimals} from '../../ec-economics';
import {PodStatisticalProfileRequestPlantType} from 'smag-commons/dto/api/profiles';
import {isConsumer, isPlant, isProducer, isProsumer, isPvPlant, isWindPlant} from '../../statistical-profiles';

const MONTHS = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December'
];

const FIRST_SEMESTER = MONTHS.slice(0, MONTHS.length / 2);
const SECOND_SEMESTER = MONTHS.slice(MONTHS.length / 2);

const SLOTS = [TimeSlot.F1, TimeSlot.F2, TimeSlot.F3];

enum EnergyType {
    ABSORBED = 'absorbed',
    INJECTED = 'injected'
}

const ENERGY_TYPES = [EnergyType.ABSORBED, EnergyType.INJECTED];

enum MonthlyEnergy {
    ABSORBED = 'absorbed_monthly_energies',
    INJECTED = 'injected_monthly_energies'
}

const MONTHLY_ENERGIES = [MonthlyEnergy.ABSORBED, MonthlyEnergy.INJECTED];

@Component({
    selector: 'app-pod-profile',
    templateUrl: './pod-profile.component.html',
    styleUrls: ['./pod-profile.component.scss']
})
export class PodProfileComponent implements OnInit, OnDestroy {

    private destroyed = new Subject<void>();

    @Input() userType: UserType;
    @Input() rateMode: RateMode;
    @Input() resource: StatisticalProfileResource;
    @Input() statisticalProfile: PodStatisticalProfile | PlantStatisticalProfile;
    @Input() podStatisticalProfileTemplates: PodStatisticalProfileTemplate[];
    @Input() pvPlantStatisticalProfileTemplates: PvPlantStatisticalProfileTemplate[];
    @Input() windPlantStatisticalProfileTemplates: WindPlantStatisticalProfileTemplate[];
    @Output() values: EventEmitter<PodStatisticalProfile | PlantStatisticalProfile> = new EventEmitter<PodStatisticalProfile | PlantStatisticalProfile>();
    @Output() valid: EventEmitter<boolean> = new EventEmitter<boolean>();

    form: FormGroup;
    totals: {} = {};
    showWarning = false;
    warningText: string = null;
    showSpinner = false;
    template: any; // PodStatisticalProfileTemplate or PvPlantStatisticalProfileTemplate or WindPlantStatisticalProfileTemplate

    readonly RateMode = RateMode;
    readonly MONTHS = MONTHS;
    readonly FIRST_SEMESTER = FIRST_SEMESTER;
    readonly SECOND_SEMESTER = SECOND_SEMESTER;
    readonly SLOTS = SLOTS;
    readonly EnergyType = EnergyType;
    readonly ENERGY_TYPES = ENERGY_TYPES;
    readonly MonthlyEnergy = MonthlyEnergy;
    readonly TimeSlot = TimeSlot;
    readonly isDefined = isDefined;
    readonly limitDecimals = limitDecimals;

    constructor(private readonly apiService: SmagApiService,
                private readonly fb: FormBuilder,
                private readonly i18n: I18NService) {
        this.form = this.fb.group({});
    }

    ngOnInit() {
        this.initForm();
        this.setTotals(this.statisticalProfile);
        this.form.controls.yearlyConsumption?.valueChanges
            .pipe(debounceTime(250), takeUntil(this.destroyed))
            .subscribe(() => this.onYearlyConsumptionChange());
        this.form.controls.yearlyHours?.valueChanges
            .pipe(debounceTime(250), takeUntil(this.destroyed))
            .subscribe(() => this.onYearlyHoursChange());

        this.form.valueChanges
            .pipe(takeUntil(this.destroyed))
            .subscribe(value => {
                const statisticalProfile = this.mkStatisticalProfile(value);
                this.setTotals(statisticalProfile);
                this.values.emit(statisticalProfile);
            });

        this.form.statusChanges
            .pipe(takeUntil(this.destroyed))
            .subscribe(() => this.valid.emit(this.form.valid));
    }

    ngOnDestroy() {
        this.destroyed.next();
        this.destroyed.complete();
    }

    onYearlyConsumptionChange() {
        // when PodStatisticalProfileTemplate.yearly_consumption is specified,
        // PodStatisticalProfileTemplate.absorbed_monthly_energies is used to hold monthly *percentages*
        let yearlyConsumption: number = null;
        const absorbedMonthlyEnergies: { [slot in TimeSlot]?: number[] } = this.template?.absorbed_monthly_energies || null;
        if (isDefined(this.form.controls.yearlyConsumption?.value)) {
            yearlyConsumption = this.form.controls.yearlyConsumption.value;
        } else if (isDefined(this.template?.yearly_consumption)) {
            yearlyConsumption = this.template.yearly_consumption;
            this.form.controls.yearlyConsumption?.setValue(yearlyConsumption);
        }
        this.setFormOnChange(absorbedMonthlyEnergies, yearlyConsumption);
    }

    onYearlyHoursChange() {
        this.setFormOnChange(null, null, this.form.controls.yearlyHours.value);
    }

    onTemplateSelectionChange(event: MatSelectChange) {
        this.template = event.value;
        if (this.isConsumer) {
            let yearlyConsumption = null;
            const absorbedMonthlyEnergies = this.template?.absorbed_monthly_energies;
            if (this.template) {
                if (isDefined(this.template.yearly_consumption)) {
                    yearlyConsumption = this.template.yearly_consumption;
                    this.form.controls.yearlyConsumption?.setValue(yearlyConsumption);
                } else if (isDefined(this.form.controls.yearlyConsumption?.value)) {
                    yearlyConsumption = this.form.controls.yearlyConsumption.value;
                }
                this.setFormOnChange(absorbedMonthlyEnergies, yearlyConsumption);
            }
        } else if (this.isWindPlant) {
            if (isDefined(this.form.controls.yearlyHours.value)) {
                this.setFormOnChange(null, null, this.form.controls.yearlyHours.value);
            }
        } else {
            this.setFormOnChange(null);
        }
    }

    get filteredTemplates() {
        if (this.isConsumer || this.isProsumer) {
            return this.podStatisticalProfileTemplates
                .filter(template =>
                    template.type === PodStatisticalProfileTemplateType.CONSUMER &&
                    template.rate_mode === this.rateMode &&
                    template.user_type === this.userType);
        }
        const pvTemplates = this.pvPlantStatisticalProfileTemplates.filter(template => template.rate_mode === this.rateMode);
        const windTemplates = this.windPlantStatisticalProfileTemplates.filter(template => template.rate_mode === this.rateMode);
        if (this.isProducer) {
            const podTemplates = this.podStatisticalProfileTemplates
                .filter(template => template.type === PodStatisticalProfileTemplateType.PRODUCER && template.rate_mode === this.rateMode);
            if (this.isPvPlant) {
                return [...pvTemplates, ...podTemplates];
            }
            if (this.isWindPlant) {
                return [...windTemplates, ...podTemplates];
            }
            return podTemplates;
        }
        if (this.isPlant) {
            if (this.isPvPlant) {
                return pvTemplates;
            }
            if (this.isWindPlant) {
                return windTemplates;
            }
            return [];
        }
        return [];
    }

    get isConsumer() {
        return isConsumer(this.resource);
    }

    get isProducer() {
        return isProducer(this.resource);
    }

    get isProsumer() {
        return isProsumer(this.resource);
    }

    get isPlant() {
        return isPlant(this.resource);
    }

    get isPvPlant() {
        return isPvPlant(this.resource);
    }

    get isWindPlant() {
        return isWindPlant(this.resource);
    }

    private initForm() {
        this.form.addControl('template', new FormControl(null));
        if (this.isConsumer) {
            this.initConsumerForm();
        } else if (this.isProducer) {
            this.initProducerForm();
        } else if (this.isPlant) {
            this.initPlantForm();
        } else if (this.isProsumer) {
            this.initProsumerForm();
        }
    }

    private mkStatisticalProfile(value: { [key: string]: number }) {
        const statisticalProfile = {rate_mode: this.rateMode} as PodStatisticalProfile | PlantStatisticalProfile;
        if (this.isPlant) {
            if (this.rateMode === RateMode.SINGLE) {
                (statisticalProfile as PlantStatisticalProfile).produced_monthly_energies = {[TimeSlot.ALL]: MONTHS.map(month => value[month])};
            } else if (this.rateMode === RateMode.HOURLY) {
                (statisticalProfile as PlantStatisticalProfile).produced_monthly_energies = {};
                SLOTS.forEach(slot => (statisticalProfile as PlantStatisticalProfile).produced_monthly_energies[slot] = MONTHS.map(month => value[`${month}_${slot}`]));
            }
        } else if (this.isProsumer) {
            (statisticalProfile as PodStatisticalProfile).user_type = this.userType;
            if (this.rateMode === RateMode.SINGLE) {
                (statisticalProfile as PodStatisticalProfile).absorbed_monthly_energies = {[TimeSlot.ALL]: MONTHS.map(month => value[`${month}_${EnergyType.ABSORBED}`])};
                (statisticalProfile as PodStatisticalProfile).injected_monthly_energies = {[TimeSlot.ALL]: MONTHS.map(month => value[`${month}_${EnergyType.INJECTED}`])};
            } else if (this.rateMode === RateMode.HOURLY) {
                (statisticalProfile as PodStatisticalProfile).absorbed_monthly_energies = {};
                (statisticalProfile as PodStatisticalProfile).injected_monthly_energies = {};
                SLOTS.forEach(slot => (statisticalProfile as PodStatisticalProfile).absorbed_monthly_energies[slot] = MONTHS.map(month => value[`${month}_${EnergyType.ABSORBED}_${slot}`]));
                SLOTS.forEach(slot => (statisticalProfile as PodStatisticalProfile).injected_monthly_energies[slot] = MONTHS.map(month => value[`${month}_${EnergyType.INJECTED}_${slot}`]));
            }
        } else {
            (statisticalProfile as PodStatisticalProfile).user_type = this.userType;
            if (this.isConsumer) {
                if (this.rateMode === RateMode.SINGLE) {
                    (statisticalProfile as PodStatisticalProfile).absorbed_monthly_energies = {[TimeSlot.ALL]: MONTHS.map(month => value[month])};
                } else if (this.rateMode === RateMode.HOURLY) {
                    (statisticalProfile as PodStatisticalProfile).absorbed_monthly_energies = {};
                    SLOTS.forEach(slot => (statisticalProfile as PodStatisticalProfile).absorbed_monthly_energies[slot] = MONTHS.map(month => value[`${month}_${slot}`]));
                }
            } else if (this.isProducer) {
                if (this.rateMode === RateMode.SINGLE) {
                    (statisticalProfile as PodStatisticalProfile).injected_monthly_energies = {[TimeSlot.ALL]: MONTHS.map(month => value[month])};
                } else if (this.rateMode === RateMode.HOURLY) {
                    (statisticalProfile as PodStatisticalProfile).injected_monthly_energies = {};
                    SLOTS.forEach(slot => (statisticalProfile as PodStatisticalProfile).injected_monthly_energies[slot] = MONTHS.map(month => value[`${month}_${slot}`]));
                }
            }
        }
        return statisticalProfile;
    }

    private setTotals(statisticalProfile: PodStatisticalProfile | PlantStatisticalProfile) {
        if (this.isPlant) {
            const profile = statisticalProfile as PlantStatisticalProfile;
            if (this.rateMode === RateMode.SINGLE) {
                this.totals[TimeSlot.ALL] = this.mkProducedTotals(profile, TimeSlot.ALL);
            } else if (this.rateMode === RateMode.HOURLY) {
                SLOTS.forEach(slot => this.totals[slot] = this.mkProducedTotals(profile, slot));
                this.totals[TimeSlot.ALL] = this.mkTotalsForTimeSlotAll();
            }
        } else if (this.isProsumer) {
            MONTHLY_ENERGIES.forEach(me => this.totals[me] = {});
            if (this.rateMode === RateMode.SINGLE) {
                MONTHLY_ENERGIES.forEach(me => this.totals[me][TimeSlot.ALL] = this.mkMonthlyEnergiesTotals(statisticalProfile, me, TimeSlot.ALL));
            } else if (this.rateMode === RateMode.HOURLY) {
                MONTHLY_ENERGIES.forEach(me => {
                    SLOTS.forEach(slot => this.totals[me][slot] = this.mkMonthlyEnergiesTotals(statisticalProfile, me, slot));
                    this.totals[me][TimeSlot.ALL] = this.mkTotalsForTimeSlotAll(me);
                });
            }
        } else { // consumer or producer
            const profile = statisticalProfile as PodStatisticalProfile;
            if (this.isConsumer) {
                if (this.rateMode === RateMode.SINGLE) {
                    this.totals[TimeSlot.ALL] = this.mkAbsorbedTotals(profile, TimeSlot.ALL);
                } else if (this.rateMode === RateMode.HOURLY) {
                    SLOTS.forEach(slot => this.totals[slot] = this.mkAbsorbedTotals(profile, slot));
                    this.totals[TimeSlot.ALL] = this.mkTotalsForTimeSlotAll();
                }
            } else if (this.isProducer) {
                if (this.rateMode === RateMode.SINGLE) {
                    this.totals[TimeSlot.ALL] = this.mkInjectedTotals(profile, TimeSlot.ALL);
                } else if (this.rateMode === RateMode.HOURLY) {
                    SLOTS.forEach(slot => this.totals[slot] = this.mkInjectedTotals(profile, slot));
                    this.totals[TimeSlot.ALL] = this.mkTotalsForTimeSlotAll();
                }
            }
        }
    }

    private mkConsumedMonthlyEnergies(yearlyConsumption: number): { [slot in TimeSlot]?: number[] } {
        const absorbedMonthlyEnergies = this.template.absorbed_monthly_energies;
        const consumedMonthlyEnergies: { [slot in TimeSlot]?: number[] } = {};
        if (this.rateMode === RateMode.SINGLE) {
            MONTHS.forEach((_, index) => (consumedMonthlyEnergies[TimeSlot.ALL] ??= [])[index] =
                absorbedMonthlyEnergies[TimeSlot.ALL][index] * (yearlyConsumption ?? 1));
        } else if (this.rateMode === RateMode.HOURLY) {
            MONTHS.forEach((_, index) =>
                SLOTS.forEach(slot =>
                    (consumedMonthlyEnergies[slot] ??= [])[index] = absorbedMonthlyEnergies[slot][index] * (yearlyConsumption ?? 1)));
        }
        return consumedMonthlyEnergies;
    }

    private mkPlants() {
        let type: PodStatisticalProfileRequestPlantType;
        switch (this.resource.type) {
            case StatisticalProfileResourceType.WIND_PLANT:
                type = PodStatisticalProfileRequestPlantType.WIND;
                break;
            case StatisticalProfileResourceType.PV_PLANT:
            default:
                type = PodStatisticalProfileRequestPlantType.PV;
                break;
        }
        return ((this.resource as StatisticalProfilePodResource).plants || [])
            .map(plant => ({profile: plant.profile, type}))
            .filter(plantProfile => !!plantProfile);
    }

    private mkWarning(err: Error) {
        this.showWarning = true;
        const code = errorCode(err);
        switch (code) {
            case ErrorCode.PV_PROFILE_MISCONFIG:
                this.warningText = this.i18n.translate('Please make sure that the statistical profile of every pv plant under this prosumer is configured correctly');
                break;
            default:
                this.warningText = this.i18n.translate('An error occurred while retrieving the template');
                break;
        }
    }

    private initConsumerForm() {
        this.form.addControl('yearlyConsumption', new FormControl(null));
        const profile = this.statisticalProfile as PodStatisticalProfile;
        if (this.rateMode === RateMode.SINGLE) {
            MONTHS.forEach((month, index) => this.form.addControl(month,
                new FormControl(this.getProfileAbsorbedMonthlyEnergies(profile, TimeSlot.ALL, index), this.getProfileValidators())));
        } else if (this.rateMode === RateMode.HOURLY) {
            MONTHS.forEach((month, index) =>
                SLOTS.forEach(slot => this.form.addControl(`${month}_${slot}`,
                    new FormControl(this.getProfileAbsorbedMonthlyEnergies(profile, slot, index), this.getProfileValidators()))));
        }
    }

    private initProducerForm() {
        const profile = this.statisticalProfile as PodStatisticalProfile;
        if (this.rateMode === RateMode.SINGLE) {
            MONTHS.forEach((month, index) => this.form.addControl(month,
                new FormControl(this.getProfileInjectedMonthlyEnergies(profile, TimeSlot.ALL, index), this.getProfileValidators())));
        } else if (this.rateMode === RateMode.HOURLY) {
            MONTHS.forEach((month, index) =>
                SLOTS.forEach(slot => this.form.addControl(`${month}_${slot}`,
                    new FormControl(this.getProfileInjectedMonthlyEnergies(profile, slot, index), this.getProfileValidators()))));
        }
    }

    private initPlantForm() {
        if (this.isWindPlant) {
            this.form.addControl('yearlyHours', new FormControl(null));
        }
        const profile = this.statisticalProfile as PlantStatisticalProfile;
        if (this.rateMode === RateMode.SINGLE) {
            MONTHS.forEach((month, index) => this.form.addControl(month,
                new FormControl(this.getProfileProducedMonthlyEnergies(profile, TimeSlot.ALL, index), this.getProfileValidators())));
        } else if (this.rateMode === RateMode.HOURLY) {
            MONTHS.forEach((month, index) =>
                SLOTS.forEach(slot => this.form.addControl(`${month}_${slot}`,
                    new FormControl(this.getProfileProducedMonthlyEnergies(profile, slot, index), this.getProfileValidators()))));
        }
    }

    private initProsumerForm() {
        this.form.addControl('yearlyConsumption', new FormControl(null));
        const profile = this.statisticalProfile as PodStatisticalProfile;
        if (this.rateMode === RateMode.SINGLE) {
            MONTHS.forEach((month, index) =>
                ENERGY_TYPES.forEach(type =>
                    this.form.addControl(`${month}_${type}`,
                        new FormControl(
                            isDefined(profile) &&
                            isDefined(profile.absorbed_monthly_energies) &&
                            isDefined(profile.injected_monthly_energies) ? (
                                type === EnergyType.ABSORBED ?
                                    this.getProfileAbsorbedMonthlyEnergies(profile, TimeSlot.ALL, index) :
                                    this.getProfileInjectedMonthlyEnergies(profile, TimeSlot.ALL, index)
                            ) : null,
                            this.getProfileValidators()))));
        } else if (this.rateMode === RateMode.HOURLY) {
            MONTHS.forEach((month, index) =>
                ENERGY_TYPES.forEach(type =>
                    SLOTS.forEach(slot =>
                        this.form.addControl(`${month}_${type}_${slot}`,
                            new FormControl(
                                isDefined(profile) &&
                                isDefined(profile.absorbed_monthly_energies) &&
                                isDefined((profile).injected_monthly_energies) ? (
                                    type === EnergyType.ABSORBED ?
                                        this.getProfileAbsorbedMonthlyEnergies(profile, slot, index) :
                                        this.getProfileInjectedMonthlyEnergies(profile, slot, index)
                                ) : null,
                                this.getProfileValidators())))));
        }
    }

    private getProfileValidators() {
        return [Validators.required, Validators.min(0)];
    }

    private getProfileAbsorbedMonthlyEnergies(profile: PodStatisticalProfile, slot: TimeSlot, index: number): number {
        return profile?.absorbed_monthly_energies[slot] ? round(profile.absorbed_monthly_energies[slot][index]) : null;
    }

    private getProfileInjectedMonthlyEnergies(profile: PodStatisticalProfile, slot: TimeSlot, index: number): number {
        return profile?.injected_monthly_energies[slot] ? round(profile.injected_monthly_energies[slot][index]) : null;
    }

    private getProfileProducedMonthlyEnergies(profile: PlantStatisticalProfile, slot: TimeSlot, index: number): number {
        return profile?.produced_monthly_energies[slot] ? round(profile.produced_monthly_energies[slot][index]) : null;
    }

    private mkProducedTotals(profile: PlantStatisticalProfile, slot: TimeSlot) {
        return isDefined(profile?.produced_monthly_energies[slot]) ?
            round(profile.produced_monthly_energies[slot].reduce((a, b) => a + b, 0)) : 0;
    }

    private mkAbsorbedTotals(profile: PodStatisticalProfile, slot: TimeSlot) {
        return isDefined(profile?.absorbed_monthly_energies[slot]) ?
            round(profile.absorbed_monthly_energies[slot].reduce((a, b) => a + b, 0)) : 0;
    }

    private mkInjectedTotals(profile: PodStatisticalProfile, slot: TimeSlot) {
        return isDefined(profile?.injected_monthly_energies[slot]) ?
            round(profile.injected_monthly_energies[slot].reduce((a, b) => a + b, 0)) : 0;
    }

    private mkTotalsForTimeSlotAll(monthlyEnergy?: MonthlyEnergy): number {
        return round(
            Object.keys(monthlyEnergy ? this.totals[monthlyEnergy] : this.totals)
                .filter(k => Object.values(SLOTS).includes(k as TimeSlot))
                .map(k => monthlyEnergy ? this.totals[monthlyEnergy][k] : this.totals[k])
                .reduce((a, b) => a + b, 0)
        );
    }

    private mkMonthlyEnergiesTotals(statisticalProfile: PodStatisticalProfile | PlantStatisticalProfile, monthlyEnergy: MonthlyEnergy, slot: TimeSlot) {
        return isDefined(statisticalProfile) && isDefined(statisticalProfile[monthlyEnergy]) && isDefined(statisticalProfile[monthlyEnergy][slot]) ?
            round(statisticalProfile[monthlyEnergy][slot].reduce((a: number, b: number) => a + b, 0)) : 0;
    }

    private setFormControlValueWithFactor(name: string,
                                          energies: { [timeSlot in TimeSlot]?: number[] },
                                          slot: TimeSlot,
                                          index: number,
                                          scaleFactor?: number) {
        const factor = scaleFactor ?? 1;
        this.form.controls[name].setValue(round(energies[slot][index] * factor));
    }

    private setFormOnChange(absorbedMonthlyEnergies: { [slot in TimeSlot]?: number[] },
                            yearlyConsumption?: number,
                            yearlyHours?: number) {
        if (this.isConsumer && absorbedMonthlyEnergies && isDefined(yearlyConsumption)) {
            if (this.rateMode === RateMode.SINGLE) {
                MONTHS.forEach((month, index) => this.setFormControlValueWithFactor(month, absorbedMonthlyEnergies, TimeSlot.ALL, index, yearlyConsumption));
            } else if (this.rateMode === RateMode.HOURLY) {
                MONTHS.forEach((month, index) =>
                    SLOTS.forEach(slot =>
                        this.setFormControlValueWithFactor(`${month}_${slot}`, absorbedMonthlyEnergies, slot, index, yearlyConsumption)));
            }
        } else if (this.isProducer) {
            // producers can use pvPlantStatisticalProfileTemplates or windPlantStatisticalProfileTemplates
            const energies = isDefined(this.template.type) ?
                (this.template as PodStatisticalProfileTemplate).injected_monthly_energies :
                (this.template as PvPlantStatisticalProfileTemplate | WindPlantStatisticalProfileTemplate).produced_monthly_energies;
            // templates values are normalized, they must be multiplied by the sum of the nominal power of all the producer's plants
            const totalNominalPower = (this.resource as StatisticalProfilePodResource).plants
                .map(plant => plant.nominalPower ?? 0)
                .reduce((acc, val) => acc + val, 0);
            if (this.rateMode === RateMode.SINGLE) {
                MONTHS.forEach((month, index) => this.setFormControlValueWithFactor(month, energies, TimeSlot.ALL, index, totalNominalPower));
            } else if (this.rateMode === RateMode.HOURLY) {
                MONTHS.forEach((month, index) =>
                    SLOTS.forEach(slot => this.setFormControlValueWithFactor(`${month}_${slot}`, energies, slot, index, totalNominalPower)));
            }
        } else if (this.isPlant) {
            // templates values are normalized, they must be multiplied by the nominal power (and yearly hours in case of a wind plant)
            if (this.isPvPlant || this.isWindPlant && isDefined(yearlyHours) && isDefined(this.template)) {
                const nominalPower = (this.resource as StatisticalProfilePlantResource).nominalPower ?? 0;
                const factor = this.isWindPlant ? nominalPower * yearlyHours : nominalPower;
                if (this.rateMode === RateMode.SINGLE) {
                    MONTHS.forEach((month, index) =>
                        this.setFormControlValueWithFactor(month, this.template.produced_monthly_energies, TimeSlot.ALL, index, factor));
                } else if (this.rateMode === RateMode.HOURLY) {
                    MONTHS.forEach((month, index) =>
                        SLOTS.forEach(slot =>
                            this.setFormControlValueWithFactor(`${month}_${slot}`, this.template.produced_monthly_energies, slot, index, factor)));
                }
            }
        } else if (this.isProsumer && absorbedMonthlyEnergies && isDefined(yearlyConsumption) && isDefined(this.template)) {
            // prosumer profile must be retrieved from api
            this.showSpinner = true;
            const request = {
                user_type: this.userType,
                rate_mode: this.rateMode,
                consumed_monthly_energies: this.mkConsumedMonthlyEnergies(yearlyConsumption),
                plants: this.mkPlants(),
                year: null
            };
            this.apiService.getProsumerPodStatisticalProfile(request)
                .pipe(
                    catchError(err => {
                        this.showSpinner = false;
                        this.mkWarning(err);
                        return EMPTY;
                    }))
                .subscribe(profile => {
                    this.showSpinner = false;
                    if (this.rateMode === RateMode.SINGLE) {
                        MONTHS.forEach((month, index) =>
                            ENERGY_TYPES.forEach(type =>
                                this.setFormControlValueWithFactor(`${month}_${type}`,
                                    type === EnergyType.ABSORBED ? profile.absorbed_monthly_energies : profile.injected_monthly_energies,
                                    TimeSlot.ALL, index)));
                    } else if (this.rateMode === RateMode.HOURLY) {
                        MONTHS.forEach((month, index) =>
                            ENERGY_TYPES.forEach(type =>
                                SLOTS.forEach(slot =>
                                    this.setFormControlValueWithFactor(`${month}_${type}_${slot}`,
                                        type === EnergyType.ABSORBED ? profile.absorbed_monthly_energies : profile.injected_monthly_energies,
                                        slot, index))));
                    }
                });
        }
    }

}
